import { IBundleBuilderItem, applySeedItem } from '@/helpers/bundle-builder-storage';
import { findSeedGroup } from '@/helpers/bundle-promotion-helper';
import { bundleSlabsQueryOptions } from '@/hooks/bundlePromotion';
import { IBundleSlab } from '@/interfaces/bundlePromotion';
import { IProductDetails } from '@/interfaces/product';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { useState } from 'react';

interface IBundleExploreSelection {
  size: string | number;
  quantity: number;
}

// PDP "Explore Bundle Offers": seeds the product into the first slab's best-matching group and opens /bundle on it.
// Resolves false (nothing written, no navigation) when it cannot, so the caller can fall back to the customer's original action.
export const useBundleOfferExplore = () => {
  const queryClient = useQueryClient();
  const router = useRouter();
  const [isExploring, setIsExploring] = useState(false);

  const explore = async (product: IProductDetails, selection: IBundleExploreSelection): Promise<boolean> => {
    setIsExploring(true);
    try {
      const slabs = await queryClient.fetchQuery<IBundleSlab[]>(bundleSlabsQueryOptions(product.SeName));
      const slab = slabs?.[0];
      if (!slab) return false;
      const group = findSeedGroup(slab, product);
      if (!group) return false;

      const variation = product.Variations?.find((x) => x.ProductAttributeValue == selection.size);
      const size = variation?.ProductAttributeValue ?? selection.size;
      const item: IBundleBuilderItem = {
        key: `${product.Id}_${size}`,
        productId: product.Id,
        seName: product.SeName,
        name: product.Name,
        sku: product.Sku,
        size,
        quantity: selection.quantity,
        unitPrice: product.ProductPrice.Price,
        imageUrl: product.ProductImageList?.find((image) => image.IsFeaturedImage)?.Url || product.ProductImageList?.[0]?.Url || '',
        attributes: variation ? [{ key: variation.ProductAttributeId, value: variation.ProductAttributeValueId }] : [],
      };
      applySeedItem(slab.Id, group.Id, item);
      router.push(`/bundle?slabId=${slab.Id}`);
      return true;
    } catch {
      return false;
    } finally {
      setIsExploring(false);
    }
  };

  return { explore, isExploring };
};
